// NarrationStream — the hero feed: DM prose (streamed live), player speech/actions,
// dice & combat "beats" (rendered from WS tool events), round dividers, slain-combatant
// markers, and a live thinking indicator. Smart autoscroll: stays pinned to the bottom
// while you're there, else surfaces a "N new" pill instead of yanking you down.
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { LogEntry, RollGroup, StreamItem } from "../lib/types";
import type { ThinkingState, TurnToast } from "../hooks/useGameSocket";
import { Markdown } from "./Markdown";

export function NarrationStream({
  stream,
  thinking,
  scene,
  turnToast,
  onDismissTurn,
}: {
  stream: StreamItem[];
  thinking: ThinkingState | null;
  scene?: string;
  // Optional — when the socket surfaces a your-turn / roll-called nudge, it renders as a
  // `.turn-toast` over the stream. Absent (default) = no toast (title flash + chime still
  // fire from the hook).
  turnToast?: TurnToast | null;
  onDismissTurn?: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const stickRef = useRef(true);        // pinned to bottom?
  const prevLenRef = useRef(0);
  const [newCount, setNewCount] = useState(0);

  const nearBottom = () => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 140;
  };
  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    stickRef.current = true;
    setNewCount(0);
    endRef.current?.scrollIntoView({ behavior, block: "end" });
  };

  // Track whether the reader is at the bottom; leaving the bottom "unpins" autoscroll.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      stickRef.current = nearBottom();
      if (stickRef.current) setNewCount(0);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // On new/growing content: follow the bottom if pinned, else tally unseen items. A
  // streaming DM entry mutates in place (length unchanged) so it keeps following without
  // inflating the "new" count.
  useEffect(() => {
    const added = stream.length - prevLenRef.current;
    prevLenRef.current = stream.length;
    if (stickRef.current) {
      endRef.current?.scrollIntoView({ behavior: added > 3 ? "auto" : "smooth", block: "end" });
    } else if (added > 0) {
      setNewCount((n) => n + added);
    }
  }, [stream, thinking]);

  // Round dividers: mark the first beat of each new combat round (round increases past
  // the first). Slain markers: a combatant dropping to 0 in a beat gets a skull line after.
  const roundMarks = useMemo(() => {
    const marks = new Map<string, number>();
    let lastRound = 0;
    for (const it of stream) {
      if (it.kind !== "tool") continue;
      const r = (typeof it.result === "object" && it.result) ? (it.result as Record<string, any>) : {};
      const round = typeof r.round === "number" ? r.round : null;
      if (round != null && round > lastRound) {
        if (lastRound > 0) marks.set(it.uid, round);
        lastRound = round;
      }
    }
    return marks;
  }, [stream]);

  return (
    <div className="stream" ref={scrollRef} style={{ position: "relative" }}>
      <div className="stream__inner">
        {scene && <div className="scene-banner">{scene}</div>}
        {stream.length === 0 && !thinking && (
          <div className="muted" style={{ textAlign: "center", padding: "40px 0", fontFamily: "var(--serif)", fontStyle: "italic", fontSize: 17 }}>
            The hearth is lit. Press <b>Begin the session</b> to open your tale…
          </div>
        )}
        {stream.map((item) => {
          const round = roundMarks.get(item.uid);
          const divider = round ? <RoundDivider key={`rd-${item.uid}`} round={round} /> : null;
          if (item.kind === "log") {
            return <Fragment key={item.uid}>{divider}<LogView entry={item.entry} /></Fragment>;
          }
          const slain = slainName(item.name, item.input, item.result);
          return (
            <Fragment key={item.uid}>
              {divider}
              <ToolBeat name={item.name} input={item.input} result={item.result} />
              {slain && <SlainBeat name={slain} />}
            </Fragment>
          );
        })}
        {thinking && (
          <div className="thinking">
            <span>{thinking.who} {thinking.role === "dm" ? "weaves the tale" : "considers"}</span>
            <span className="thinking__dots"><i /><i /><i /></span>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {newCount > 0 && (
        <button className="new-events-pill" onClick={() => scrollToBottom()}
          style={{ position: "sticky", bottom: 12, left: "50%", transform: "translateX(-50%)", display: "block" }}>
          ↓ {newCount} new
        </button>
      )}

      {turnToast && (
        <div key={turnToast.nonce} className="turn-toast" role="status"
          onClick={onDismissTurn}
          style={{ position: "sticky", bottom: 12, left: 0, right: 0, margin: "0 auto", width: "fit-content", cursor: "pointer" }}>
          🎲 {turnToast.text}
        </div>
      )}
    </div>
  );
}

// A combatant fell — a full-width skull marker after the beat that dropped them.
function SlainBeat({ name }: { name: string }) {
  return (
    <div className="slain-beat"
      style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center",
               padding: "6px 0", opacity: 0.85, fontFamily: "var(--serif)", fontStyle: "italic" }}>
      <span aria-hidden>💀</span><span>{name} falls</span>
    </div>
  );
}

// Full-width round divider between combat rounds.
function RoundDivider({ round }: { round: number }) {
  return (
    <div className="round-divider"
      style={{ display: "flex", alignItems: "center", gap: 12, margin: "14px 0", textTransform: "uppercase",
               letterSpacing: "0.14em", fontSize: 12, fontWeight: 700, opacity: 0.7 }}>
      <span style={{ flex: 1, height: 1, background: "currentColor", opacity: 0.25 }} />
      <span>⚔ Round {round}</span>
      <span style={{ flex: 1, height: 1, background: "currentColor", opacity: 0.25 }} />
    </div>
  );
}

// Best-effort: did this beat drop a combatant to 0? Returns the name or null. Reads only
// fields we know are player-safe (never a foe's exact HP) — a 0/"down" is public.
function slainName(name: string, input: Record<string, any>, result: Record<string, any> | string | number | null): string | null {
  const r = (typeof result === "object" && result) ? result as Record<string, any> : {};
  if (name === "apply_damage" && r.current_hp != null && r.current_hp <= 0) return String(input.target || r.target || "A combatant");
  if (name === "next_turn" && r.death_save && r.death_save.outcome === "dead") return String(r.name || "A combatant");
  if (name === "attack" && (r.dropped || r.target_dropped || r.slain)) return String(r.target || input.target || "A combatant");
  return null;
}

function LogView({ entry }: { entry: LogEntry }) {
  const isAi = entry.data && (entry.data as any).ai === true;
  // A DM entry still receiving narration_delta chunks — show a live caret until the
  // final 'log' event finalizes it (which clears the streaming flag).
  const streaming = !!(entry.data && (entry.data as any).streaming === true);
  const cls = `entry entry--${entry.kind}${isAi ? " ai" : ""}${streaming ? " entry--streaming is-streaming" : ""}`;
  const showAttr = entry.kind === "speech" || entry.kind === "action" || entry.kind === "dm";
  const attr = entry.kind === "dm" ? "Dungeon Master" : entry.speaker;
  const useMarkdown = entry.kind === "dm" || entry.kind === "narration" || entry.kind === "speech";
  return (
    <div className={cls}>
      {showAttr && attr && (
        <div className="e-attr">
          {entry.kind === "dm" && "✦ "}
          {attr}
          {isAi && <span className="chip chip--ai" style={{ marginLeft: 4 }}>AI</span>}
        </div>
      )}
      <div className="e-prose">
        {useMarkdown ? <Markdown text={entry.text} /> : entry.text}
        {streaming && (
          <span className="stream-caret" aria-hidden
            style={{ display: "inline-block", width: "0.5ch", marginLeft: 2,
                     borderRight: "2px solid currentColor", animation: "hearthBlink 1s step-end infinite" }} />
        )}
      </div>
    </div>
  );
}

// ── tool beats ────────────────────────────────────────────────────────────
function ToolBeat({
  name,
  input,
  result,
}: {
  name: string;
  input: Record<string, any>;
  result: Record<string, any> | string | number | null;
}) {
  const r = (typeof result === "object" && result) ? result as Record<string, any> : {};

  if (name === "roll_dice" || (name === "ability_check" && r.natural != null)) {
    const groups: RollGroup[] = r.groups || [];
    const crit = r.crit || r.natural === 20;
    const fumble = r.natural === 1;
    const reason = r.reason || input.reason || (name === "ability_check" ? `${input.skill || input.ability || ""} check` : "Roll");
    return (
      <div className={`tool-beat tool-beat--roll${crit ? " tool-beat--crit roll-crit" : ""}${fumble ? " tool-beat--fumble roll-fumble" : ""}`}
        style={crit || fumble ? { transform: "scale(1.035)", transformOrigin: "left center" } : undefined}>
        <span className="tool-beat__icon">🎲</span>
        <div className="tool-beat__body">
          <div className="tool-beat__title">{cap(reason)}{r.dc != null && ` · DC ${r.dc}`}</div>
          <div className="tool-beat__detail">
            <DiceFaces groups={groups} />
          </div>
        </div>
        <div className="beat-total">{r.total}{r.success != null && (r.success ? " ✓" : " ✗")}</div>
      </div>
    );
  }

  if (name === "player_roll") {
    const crit = r.natural === 20;
    const fumble = r.natural === 1;
    const reason = r.reason || input.reason || "Check";
    const bonus = typeof r.bonus === "number" ? r.bonus : 0;
    return (
      <div className={`tool-beat tool-beat--roll${crit ? " tool-beat--crit roll-crit" : ""}${fumble ? " tool-beat--fumble roll-fumble" : ""}`}
        style={crit || fumble ? { transform: "scale(1.035)", transformOrigin: "left center" } : undefined}>
        <span className="tool-beat__icon">🎲</span>
        <div className="tool-beat__body">
          <div className="tool-beat__title">{cap(reason)}{r.character ? ` · ${r.character}` : ""}</div>
          <div className="tool-beat__detail">
            d20 {r.natural} {bonus >= 0 ? "+" : "−"} {Math.abs(bonus)}
            {crit && " · natural 20!"}{fumble && " · natural 1…"}
          </div>
        </div>
        {/* total only — NO DC, NO pass/fail: the difficulty stays the DM's secret */}
        <div className="beat-total">{r.total}</div>
      </div>
    );
  }

  if (name === "attack") {
    const hit = r.hit;
    const crit = r.crit;
    const dmg = r.damage?.applied;
    const riders: string[] = Array.isArray(r.riders) ? r.riders : [];
    const extra: any[] = Array.isArray(r.extra) ? r.extra : [];
    const extraTotal = extra.reduce((s, e) => s + (e.applied ?? e.rolled ?? 0), 0);
    const cls = crit ? "tool-beat--crit" : hit ? "tool-beat--hit" : "tool-beat--miss";
    const totalDmg = (dmg ?? 0) + extraTotal;
    return (
      <div className={`tool-beat ${cls}`}>
        <span className="tool-beat__icon">{crit ? "💥" : hit ? "⚔️" : "🛡️"}</span>
        <div className="tool-beat__body">
          <div className="tool-beat__title">
            {r.attacker} → {r.target}: {crit ? "CRITICAL HIT" : hit ? "Hit" : "Miss"}
            {riders.map((rd) => <span key={rd} className="beat-rider">{rd}</span>)}
          </div>
          <div className="tool-beat__detail">
            {r.weapon || input.label || "Attack"} · {r.to_hit_total} vs AC {r.target_ac}
            {dmg != null && hit && ` · ${dmg} ${r.damage?.type || ""} dmg`}
            {extra.map((e, i) => (
              <span key={i} className="beat-extra">
                {" "}+ {e.applied ?? e.rolled} {e.type || ""} ({e.expression || ""})
              </span>
            ))}
          </div>
        </div>
        {totalDmg > 0 && hit && <div className="beat-total" style={{ color: "var(--crimson-bright)" }}>-{totalDmg}</div>}
      </div>
    );
  }

  if (name === "cast_spell") {
    return <CastBeat input={input} result={r} />;
  }

  if (name === "feature") {
    return <FeatureBeat result={r} />;
  }

  if (name === "saving_throw") {
    // dc/success may be redacted from the payload (DM-secret checks) — never render
    // "DC undefined" or a false ✗; show only what the table is allowed to see.
    const known = r.success != null;
    return (
      <div className={`tool-beat tool-beat--roll${known ? (r.success ? " tool-beat--hit" : " tool-beat--miss") : ""}`}>
        <span className="tool-beat__icon">🛡️</span>
        <div className="tool-beat__body">
          <div className="tool-beat__title">{cap(input.target || r.target || "")} · {String(input.ability || "").toUpperCase()} Save</div>
          <div className="tool-beat__detail">{r.total}{r.dc != null && ` vs DC ${r.dc}`}{known && ` — ${r.success ? "Success" : "Fail"}`}</div>
        </div>
        {known && <div className="beat-total">{r.success ? "✓" : "✗"}</div>}
      </div>
    );
  }

  if (name === "apply_damage") {
    return (
      <div className="tool-beat tool-beat--damage">
        <span className="tool-beat__icon">🔥</span>
        <div className="tool-beat__body">
          <div className="tool-beat__title">{cap(input.target || "")} takes damage</div>
          <div className="tool-beat__detail">{r.applied} {input.damage_type || ""} ({r.modifier}) → {r.current_hp}/{r.max_hp} HP</div>
        </div>
        <div className="beat-total" style={{ color: "var(--crimson-bright)" }}>-{r.applied}</div>
      </div>
    );
  }

  if (name === "heal") {
    return (
      <div className="tool-beat tool-beat--hit">
        <span className="tool-beat__icon">✨</span>
        <div className="tool-beat__body">
          <div className="tool-beat__title">{cap(input.target || "")} is healed</div>
          <div className="tool-beat__detail">+{r.healed} HP → {r.current_hp}</div>
        </div>
        <div className="beat-total" style={{ color: "var(--jade)" }}>+{r.healed}</div>
      </div>
    );
  }

  if (name === "start_combat") {
    return (
      <div className="tool-beat tool-beat--combat">
        <span className="tool-beat__icon">⚔️</span>
        <div className="tool-beat__body">
          <div className="tool-beat__title">Combat begins — {r.encounter || input.name || "Battle"}</div>
          <div className="tool-beat__detail">
            Initiative: {(r.initiative || []).map((x: any) => `${x.name} ${x.total}`).join(" · ")}
          </div>
        </div>
      </div>
    );
  }

  if (name === "next_turn") {
    return (
      <div className="tool-beat tool-beat--combat">
        <span className="tool-beat__icon">↻</span>
        <div className="tool-beat__body">
          <div className="tool-beat__title">{r.name ? `${r.name}'s turn` : "Next turn"}{r.round ? ` · Round ${r.round}` : ""}</div>
          {r.death_save && <div className="tool-beat__detail">Death save: {r.death_save.outcome}</div>}
        </div>
      </div>
    );
  }

  if (name === "end_combat") {
    return (
      <div className="tool-beat tool-beat--combat">
        <span className="tool-beat__icon">🏆</span>
        <div className="tool-beat__body"><div className="tool-beat__title">Combat ends — {r.rounds} rounds</div></div>
      </div>
    );
  }

  if (name === "set_condition") {
    return (
      <div className="tool-beat">
        <span className="tool-beat__icon">🌀</span>
        <div className="tool-beat__body">
          <div className="tool-beat__title">{cap(input.target || "")} {input.remove ? "recovers from" : "is"} {input.condition}</div>
        </div>
      </div>
    );
  }

  if (name === "award") {
    return (
      <div className="tool-beat tool-beat--crit">
        <span className="tool-beat__icon">💰</span>
        <div className="tool-beat__body">
          <div className="tool-beat__title">Rewards granted</div>
          <div className="tool-beat__detail">
            {input.xp ? `${input.xp} XP ` : ""}{input.gold ? `${input.gold} gp ` : ""}{input.item || ""}
          </div>
        </div>
      </div>
    );
  }

  if (name === "update_quest") {
    return (
      <div className="tool-beat">
        <span className="tool-beat__icon">📜</span>
        <div className="tool-beat__body">
          <div className="tool-beat__title">
            {input.quest_add ? `New quest: ${input.quest_add}` :
             input.quest_complete ? `Quest complete: ${input.quest_complete}` :
             input.scene ? "The scene shifts" : "World updated"}
          </div>
          {input.scene && <div className="tool-beat__detail">{input.scene}</div>}
        </div>
      </div>
    );
  }

  if (name === "lookup") return null; // internal — don't clutter the stream
  if (name === "get_state") return null;

  // Generic fallback.
  return (
    <div className="tool-beat">
      <span className="tool-beat__icon">⚙️</span>
      <div className="tool-beat__body"><div className="tool-beat__title">{name}</div></div>
    </div>
  );
}

// ── cast_spell beat: spell name + per-target save / damage / condition lines ──
function CastBeat({ input, result }: { input: Record<string, any>; result: Record<string, any> }) {
  const cast = (result.cast && typeof result.cast === "object") ? result.cast : {};
  const effect = (result.effect && typeof result.effect === "object") ? result.effect : null;
  const spellName = cast.spell || effect?.spell || input.spell || "a spell";
  const castAt = effect?.cast_at ?? cast.cast_at_level;
  const isCantrip = cast.is_cantrip;
  const targets: any[] = Array.isArray(effect?.targets) ? effect!.targets : [];
  const anyHit = targets.some((t) => t.hit || (t.success === false) || t.damage || t.healed || t.condition_applied);
  const cls = anyHit ? "tool-beat--hit" : "tool-beat--roll";
  return (
    <div className={`tool-beat tool-beat--cast ${cls}`}>
      <span className="tool-beat__icon">✦</span>
      <div className="tool-beat__body">
        <div className="tool-beat__title">
          {cap(String(spellName))}
          <span className="beat-rider beat-rider--arcane">
            {isCantrip ? "cantrip" : castAt ? `L${castAt} slot` : "cast"}
          </span>
          {cast.concentration && <span className="chip chip--conc" style={{ marginLeft: 6 }}>◎ concentration</span>}
        </div>
        {targets.length === 0 ? (
          <div className="tool-beat__detail">{effect?.note || "resolved"}</div>
        ) : (
          <div className="beat-targets">
            {targets.map((t, i) => <CastTargetLine key={i} t={t} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function CastTargetLine({ t }: { t: Record<string, any> }) {
  const parts: string[] = [];
  if (t.save != null) parts.push(`${String(t.ability || "").toUpperCase()} save ${t.save} vs DC ${t.dc} — ${t.success ? "saved" : "failed"}`);
  if (t.to_hit_total != null) parts.push(`${t.to_hit_total} vs AC ${t.ac} — ${t.hit ? (t.crit ? "crit!" : "hit") : "miss"}`);
  if (t.damage) parts.push(`${t.damage.applied} ${t.damage.type || ""} dmg`);
  if (t.healed != null) parts.push(`+${t.healed} HP`);
  if (t.condition_applied) parts.push(`→ ${t.condition_applied}`);
  return (
    <div className="beat-target-line">
      <span className="beat-target-name">{t.target}</span>
      <span className="beat-target-detail">{parts.join(" · ") || "affected"}</span>
    </div>
  );
}

// ── feature beat: feature name + mechanical effect ──
function FeatureBeat({ result }: { result: Record<string, any> }) {
  const name = result.feature || "Feature";
  const remaining = result.remaining;
  let detail = "";
  if (result.healed != null) detail = `heals ${result.healed} HP${result.detail ? ` (${result.detail})` : ""}`;
  else if (result.effect) detail = String(result.effect);
  return (
    <div className="tool-beat tool-beat--feature">
      <span className="tool-beat__icon">★</span>
      <div className="tool-beat__body">
        <div className="tool-beat__title">
          {name}
          {remaining != null && <span className="cb-feature__uses" style={{ marginLeft: 6 }}>×{remaining} left</span>}
        </div>
        {detail && <div className="tool-beat__detail">{detail}</div>}
      </div>
      {result.healed != null && <div className="beat-total" style={{ color: "var(--jade)" }}>+{result.healed}</div>}
    </div>
  );
}

function DiceFaces({ groups }: { groups: RollGroup[] }) {
  if (!groups || groups.length === 0) return <span>—</span>;
  return (
    <span className="dice-mini">
      {groups.map((g, gi) => (
        <span key={gi} style={{ display: "inline-flex", gap: 3, marginRight: 6 }}>
          {g.kept.map((v, i) => (
            <span key={`k${i}`} className={`die-face${v === g.sides ? " max" : ""}${v === 1 && g.sides === 20 ? " one" : ""}`}>{v}</span>
          ))}
          {g.dropped.map((v, i) => (
            <span key={`d${i}`} className="die-face dropped">{v}</span>
          ))}
        </span>
      ))}
    </span>
  );
}

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
